import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import AppraisalCertificate from "./AppraisalCertificate";
import { pdf } from "@react-pdf/renderer";
import { API_BASE_URL } from "../config/config";
import "./styles.css";
import { useLocation } from "react-router-dom";

const AppraisalRequest = () => {
  const location = useLocation();
  const [contactId, setContactId] = useState(location.state?.contactId || ""); // Default to state contactId
  const [formData, setFormData] = useState({
    condition: "",
    grade: "",
    Color: "",
    "Year Make Model": "", // Add this field to the initial state
    valuationDate: "", // Add valuationDate to the initial state
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [condition, setCondition] = useState("");
  const [grade, setGrade] = useState("");
  const navigate = useNavigate();
  const [previewImageSrc, setPreviewImageSrc] = useState(
    "/previewButtonHeader.svg"
  );
  const [saveButtonImageSrc, setSaveButtonImageSrc] = useState(
    "/saveButtonHeader.svg"
  );
  const [isLoading, setIsLoading] = useState(false);
  const [onClickHandler, setOnClickHandler] = useState(() => () => {});
  const [savedFormsImageSrc, setSavedFormsImageSrc] = useState(
    "/savedFormsButtomHeader.svg"
  );
  const [notes, setNotes] = useState([]); // Initialize as an empty array
  const [color, setColor] = useState(""); // State to store the color input
  const [files, setFiles] = useState([]); // State to store fetched files
  const [selectedImages, setSelectedImages] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false); // Add this state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState("");

  Modal.setAppElement("#root"); // Required for accessibility

  //HANDLE FUNCTIONS

  const handleFetchObject = async () => {
    if (!contactId) return;

    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/api/crm/get-contact?contactId=${contactId}`
      );
      if (!response.ok) throw new Error("Failed to fetch contact data");

      const data = await response.json();
      if (!data.ContactId) {
        setError("No contact found with the given ID.");
        setFormData(null);
        return;
      }

      setFormData({
        ...data,
        color: data.color || "", // Provide a default value if not present
        notes: notes || "",
      });

      await fetchNotes(contactId);
    } catch (error) {
      console.error("Error fetching contact:", error);
      setError("Error fetching contact data.");
    } finally {
      setLoading(false);
    }
  };

  const handleColorChange = (e) => {
    const newColor = e.target.value;
    setColor(newColor); // Update the color state
    setFormData((prevFormData) => ({
      ...prevFormData,
      Color: newColor, // Update formData with the new color
    }));
  };

  const handleRemoveImage = (index) => {
    setSelectedImages((prevImages) => prevImages.filter((_, i) => i !== index));
  };

  const handleGenerateAppraisalCertificate = async () => {
    const currentDate = new Date().toLocaleDateString("en-US");
    setIsLoading(true);

    try {
      // Pull the actual fields directly from formData:
      const vinValue = formData["VIN Number"]; // "VIN Number" key
      const ymmValue = formData["Year Make Model"]; // "YEAR MAKE MODEL" key

      const pdfBlob = await pdf(
        <AppraisalCertificate
          formData={formData}
          notes={notes}
          selectedImages={selectedImages}
          currentDate={currentDate}
          condition={condition}
          grade={grade}
        />
      ).toBlob();

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;

      // Build the filename from those two exact fields:
      const fileName = `${vinValue} - ${ymmValue}.pdf`;
      link.download = fileName;

      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating the PDF:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateAndUploadAppraisalCertificate = async () => {
    console.log("Generating and uploading PDF with data:", {
      formData,
      notes,
      selectedImages,
      currentDate: new Date().toLocaleDateString("en-US"),
      condition,
      grade,
    });

    const currentDate = new Date().toLocaleDateString("en-US"); // Format as MM/DD/YYYY
    setIsLoading(true);

    try {
      // Generate the PDF blob
      const pdfBlob = await pdf(
        <AppraisalCertificate
          formData={formData}
          notes={notes}
          selectedImages={selectedImages}
          currentDate={currentDate}
          condition={condition}
          grade={grade}
        />
      ).toBlob();

      // Prepare FormData for uploading
      const formDataToUpload = new FormData();
      formDataToUpload.append("pdfFile", pdfBlob, "AppraisalCertificate.pdf"); // Updated to match 'pdfFile' field in backend
      formDataToUpload.append("formData", JSON.stringify(formData)); // Attach form data as JSON

      // Send the PDF and form data to the backend
      const response = await fetch(
        `${API_BASE_URL}/api/saveCertificate`,
        {
          method: "POST",
          body: formDataToUpload,
        }
      );

      if (response.ok) {
        const { formData: savedFormData } = await response.json();
        console.log("Form and PDF saved successfully:", savedFormData);
        alert(
          `Form and PDF saved successfully! PDF URL: ${savedFormData.pdfUrl}`
        );
      } else {
        const error = await response.text();
        throw new Error(error);
      }
    } catch (error) {
      console.error("Error generating or uploading the PDF:", error);
      alert("Failed to save the form and upload the PDF");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageSelect = async (file) => {
    try {
      console.log("File selected:", file);
      const proxiedUrl = `${API_BASE_URL}/proxy-file?fileUrl=${encodeURIComponent(
        file.FileUrl
      )}`;
      console.log("Proxied URL:", proxiedUrl);

      const base64Image = await fetchBase64Image(proxiedUrl);
      console.log("Base64 image fetched:", base64Image);

      setSelectedImages((prevImages) => {
        if (prevImages.length >= 6) {
          alert("You can only select up to 6 images.");
          return prevImages;
        }
        return [...prevImages, base64Image];
      });
    } catch (error) {
      console.error("Error selecting image:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value, // Dynamically update the specific field
    }));
  };

  const handleNotesChange = (event) => {
    const updatedNotes = event.target.value;

    const formattedNotes = updatedNotes.split("\n").filter(Boolean);

    console.log("Formatted Notes:", formattedNotes); // Debugging log
    setNotes(formattedNotes);
    setFormData((prevFormData) => ({
      ...prevFormData,
      Notes: formattedNotes,
    }));
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const maxImages = 6;
    const newImages = [];

    for (const file of files) {
      if (
        Array.isArray(selectedImages) &&
        selectedImages.length + newImages.length >= maxImages
      ) {
        alert(`You can only upload up to ${maxImages} images.`);
        break;
      }

      const formData = new FormData();
      formData.append("image", file);

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/upload-certificate-image`,
          {
            method: "POST",
            body: formData,
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          console.error("Error uploading image:", errorData.error);
          continue;
        }

        const data = await response.json();
        newImages.push(data.imageUrl);
      } catch (error) {
        console.error("Error uploading image:", error.message);
      }
    }

    setSelectedImages((prevImages) => {
      const combinedImages = [
        ...(Array.isArray(prevImages) ? prevImages : []),
        ...newImages,
      ];
      return combinedImages.slice(0, maxImages);
    });
  };

  const handleApiImageSelect = (imageUrl) => {
    setSelectedImages(imageUrl); // Store the image URL directly
  };

  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append("image", file);

    console.log("File being uploaded:", file); // Log file details
    console.log("FormData content:", [...formData]); // Log all FormData content

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/upload-certificate-image`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();
      if (response.ok) {
        console.log("Response from backend:", data); // Log backend response
      } else {
        console.error("Error response from backend:", data.error);
      }
    } catch (error) {
      console.error("Error during upload:", error);
    }
  };

  const handleSaveAndUpload = async (event) => {
    event.preventDefault(); // Prevent default form submission behavior

    setIsLoading(true);

    try {
      // Generate the PDF blob
      const pdfBlob = await pdf(
        <AppraisalCertificate
          formData={formData}
          notes={notes}
          selectedImages={selectedImages} // Pass all selected images
          currentDate={new Date().toLocaleDateString("en-US")}
          condition={condition}
          grade={grade}
        />
      ).toBlob();

      // Prepare FormData for uploading
      const formDataToUpload = new FormData();
      formDataToUpload.append("pdfFile", pdfBlob, "AppraisalCertificate.pdf");
      formDataToUpload.append(
        "formData",
        JSON.stringify({
          ...formData,
          images: selectedImages, // Include all image URLs
          notes: Array.isArray(notes) ? notes.join("\n") : notes,
        })
      );

      // Send the data to the backend
      const response = await fetch(
        `${API_BASE_URL}/api/saveCertificate`,
        {
          method: "POST",
          body: formDataToUpload,
        }
      );

      if (response.ok) {
        const { formData: savedFormData } = await response.json();
        console.log("Form and PDF saved successfully:", savedFormData);
        alert(`Certificate saved successfully`);
      } else {
        const errorMessage = await response.text();
        console.error("Save failed:", errorMessage);
        alert("Failed to save the form and upload the PDF.");
      }
    } catch (error) {
      console.error("Error during save or upload process:", error);
      alert(
        "An error occurred while saving the form or uploading the PDF. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  //HANDLE FUNCTIONS

  //OTHER FUNCTIONS

  const assignHandler = () => {
    setOnClickHandler(() => {
      console.log("Button clicked!");
    });
  };

  const saveCertificateData = async (event) => {
    event.preventDefault();

    // Check notes before sending to backend
    console.log("Notes before saving:", notes); // Check here

    const notesToSave = Array.isArray(notes) ? notes.join("\n") : "";

    const dataToSave = {
      ...formData,
      notes: notesToSave, // Make sure it's 'notes' (lowercase)
      contactId,
      certificateImageName: selectedImages?.name || "",
    };

    console.log("Data being sent to backend:", dataToSave); // Debugging

    const response = await fetch(
      `${API_BASE_URL}/api/saveCertificate`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dataToSave),
      }
    );

    if (response.ok) {
      const result = await response.json();
      console.log("Save successful:", result);
      alert("Certificate saved successfully!");
    } else {
      const errorMessage = await response.text();
      console.error("Save failed:", errorMessage);
      alert("Failed to save certificate");
    }
  };

  const getCurrentDate = () => {
    return new Date().toLocaleDateString(); // or toLocaleString(), or any format you prefer
  };

  const generatePdfPreview = async () => {
    const currentDate = getCurrentDate();

    // Debugging
    console.log("Generating PDF with Notes:", notes);
    console.log("Date for PDF preview:", currentDate);

    const formattedNotes = Array.isArray(notes) ? notes : [notes];

    try {
      const pdfBlob = await pdf(
        <AppraisalCertificate
          formData={formData}
          notes={formattedNotes}
          selectedImages={selectedImages}
          currentDate={currentDate} // pass date as prop
        />
      ).toBlob();

      const url = URL.createObjectURL(pdfBlob);
      setPdfBlobUrl(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const openModal = async () => {
    await generatePdfPreview(); // Generate PDF before opening modal
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl); // Free up memory
  };

  //OTHER FUNCTIONS

  //FETCH FUNCTIONS

  const fetchNotes = async (contactId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/crm/get-notes?contactId=${contactId}`
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch notes for contactId: ${contactId}`);
      }

      const data = await response.json();
      console.log("Fetched Notes Data from API:", data);

      if (Array.isArray(data.Results) && data.Results.length > 0) {
        const allNotes = data.Results.map((note) => note.Note).filter(
          (note) => note
        );
        console.log("Formatted Notes:", allNotes);
        setNotes(allNotes);
      } else {
        console.log("No notes found for this contact.");
        setNotes([]);
      }
    } catch (error) {
      console.error("Error fetching notes:", error.message);
      setNotes([]);
    }
  };

  const fetchFiles = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/crm/get-files?contactId=${contactId}`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch files");
      }

      const data = await response.json();
      console.log("Fetched Files:", data);

      const imageFiles = Array.isArray(data.files)
        ? data.files.filter((file) => {
            const fileType = file.FileType?.toLowerCase();
            const isValidImage =
              file.FileUrl && ["png", "jpg", "jpeg"].includes(fileType);
            console.log(
              `FileUrl: ${file.FileUrl}, IsValidImage: ${isValidImage}`
            );
            return isValidImage;
          })
        : [];

      if (imageFiles.length === 0) {
        setError("No CRM images related to this contact");
      } else {
        setError("");
        setFiles(imageFiles);
      }
    } catch (error) {
      console.error("Error fetching files:", error.message);
      setError("Failed to fetch files");
    }
  };

  const fetchBase64Image = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch image. Status: ${response.status}`);
      }
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result); // Returns base64 string
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error in fetchBase64Image:", error);
      throw error;
    }
  };

  //FETCH FUNCTIONS

  //USEEEFECTS

  useEffect(() => {
    const fetchData = async () => {
      const response = await fetch(`${API_BASE_URL}/api/endpoint`);
      const data = await response.json();
      console.log("Fetched Data:", data);
      setFormData(data); // Ensure this sets the fetched data properly
    };

    fetchData();
  }, []);

  useEffect(() => {
    console.log("formData updated:", formData);
  }, [formData]);

  useEffect(() => {
    if (contactId) {
      handleFetchObject(); // Automatically fetch contact data
    }
  }, [contactId]);

  useEffect(() => {
    if (contactId) {
      fetchFiles(); // Fetch files when contactId is available
    }
  }, [contactId]);

  useEffect(() => {
    if (!notes) {
      setNotes(""); // Ensure notes always have a default value
    }
  }, [notes]);

  //USEEEFECTS

  return (
    <div
      style={{
        background:
          "linear-gradient(237deg, rgba(39, 39, 39, 0) 70%, rgba(0, 195, 116, 0.4) 120%), #272727",
        backgroundAttachment: "fixed", // This fixes the background in place
        backgroundSize: "cover", // Ensure the background covers the whole page
        minHeight: "100vh", // Ensure the container takes at least the full viewport height
        color: "#fff", // Ensure text color contrasts with background
        display: "flex",
        flexDirection: "column", // Stack elements vertically
        justifyContent: "center", // Center content vertically
        alignItems: "center", // Center content horizontally
        textAlign: "center", // Align text in the center
      }}
    >
      <header>
        <Link to="/">
          <div className="logo">
            <img src="/ae.svg" alt="Logo" className="header-image" />
          </div>
        </Link>

        <div className="actions">
          <button
            className="export"
            onClick={handleGenerateAppraisalCertificate}
            disabled={isLoading}
          >
            <img className="download-icon" src="/image 4.png" alt="Export" />
            {isLoading ? "Export" : "Export"}
          </button>
          <button className="clear" onClick={onClickHandler}>
            Clear
          </button>

          <button
            className="google-button"
            onClick={handleSaveAndUpload} // Updated to your chosen function name
            onMouseEnter={() => setSaveButtonImageSrc("/saveFormHover.svg")}
            onMouseLeave={() => setSaveButtonImageSrc("/saveButtonHeader.svg")}
          >
            <img src={saveButtonImageSrc} alt="Save" />
          </button>

          <button
            className="otherButtons"
            onClick={openModal}
            // Change image on mouse enter and leave
            onMouseEnter={() => setPreviewImageSrc("/previewButtonHover.svg")} // Change to the new image on hover
            onMouseLeave={() => setPreviewImageSrc("/previewButtonHeader.svg")} // Change back to the original image
          >
            <img src={previewImageSrc} alt="Preview" />
          </button>

          <button
            className="otherButtons"
            onClick={async () => {
              window.scrollTo(0, 0); // Scroll to the top of the page
              navigate("/SearchCertificate"); // Redirect to /forms
            }}
            onMouseEnter={() => setSavedFormsImageSrc("/savedFormsHover.svg")}
            onMouseLeave={() =>
              setSavedFormsImageSrc("/savedFormsButtomHeader.svg")
            }
          >
            <img src={savedFormsImageSrc} alt="Saved Forms" />
          </button>
        </div>
      </header>

      <div className="containerRequest">
        <div className="titleDiv">
          <h1 className="bigTitle">Appraisal Report</h1>
          <p className="subTitle">
            Review the data and generate your certificate of appraisal.
          </p>
        </div>

        <div className="form-section display-section">
          <div className="contactInfo">
            <label className="form-label" htmlFor="contactId">
              Contact Details:
            </label>
            <div className="contact-details">
              <span className="contact-id">ID: {contactId}</span>
            </div>
            <div className="contact-details">
              <span className="contact-id">
                Name: {formData?.Name?.FirstName || "First Name"}{" "}
                {formData?.Name?.LastName || "Last Name"}
              </span>
            </div>
          </div>
        </div>

        <section className="form-section">
          <h2 className="titlesCertificate">Claim Information</h2>
          <form>
            <div className="form-group">
              <label className="titleForInformation" htmlFor="vehicleOwner">
                Yeak / Make / Model
              </label>
              <input
                className="vehicleOwner"
                type="text"
                name="Year Make Model" // This matches the key in formData
                value={formData["Year Make Model"] || ""} // Controlled input
                onChange={handleInputChange} // Updates the specific field
              />
            </div>

            <div className="form-group">
              <label className="titleForInformation" htmlFor="fileNumber">
                VIN
              </label>
              <input
                className="fileNumber"
                type="text"
                id="vin"
                maxLength={17} // Limit the input to 17 characters
                name="VIN Number"
                value={formData?.["VIN Number"] || ""}
                onChange={handleInputChange}
              />
            </div>

            {/*HERE THE DIVS GOTTA APPEAR half  */}

            <div className="halfInputContainers">
              <div className="twoInputs">
                <div className="form-group">
                  <label
                    className="titleForInformation"
                    htmlFor="valuationDate"
                  >
                    Valuation Date
                  </label>
                  <input
                    className="halfInput styledDateInput"
                    type="date"
                    name="valuationDate" // Match the key in formData
                    value={formData?.["valuationDate"] || ""} // Controlled input
                    onChange={handleInputChange} // Update state dynamically
                  />
                </div>
                <div className="form-group" style={{ left: "-39px" }}>
                  <label
                    className="titleForInformation"
                    htmlFor="actualCashValue"
                  >
                    Actual Cash Value
                  </label>
                  <input
                    className="halfInput"
                    type="text"
                    name="Actual Cash Value"
                    value={
                      formData?.["Actual Cash Value"]
                        ? `$${formData["Actual Cash Value"].replace(
                            /\B(?=(\d{3})+(?!\d))/g,
                            ","
                          )}` // Add $ and format with commas
                        : ""
                    }
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9.]/g, ""); // Strip non-numeric characters
                      setFormData((prevFormData) => ({
                        ...prevFormData,
                        "Actual Cash Value": rawValue, // Save the raw numeric value without formatting
                      }));
                    }}
                  />
                </div>
              </div>

              <div className="twoInputs">
                <div className="form-group">
                  <label
                    className="titleForInformation"
                    htmlFor="odometerReading"
                  >
                    Odometer Reading
                  </label>
                  <input
                    className="halfInput"
                    type="text"
                    name="Miles" // Add the name attribute
                    value={formData?.["Miles"] || ""}
                    onChange={handleInputChange}
                  />
                </div>
                <div
                  className="form-group"
                  style={{
                    left: "-39px",
                  }} /* Adjust negative margin to move left */
                >
                  <label className="titleForInformation" htmlFor="color">
                    Color
                  </label>
                  <input
                    className="halfInput"
                    type="text"
                    name="Vehicle Color" // Match the key in formData
                    placeholder="Insert Color"
                    value={formData?.["Vehicle Color"] || ""} // Controlled component
                    onChange={(e) =>
                      setFormData((prevFormData) => ({
                        ...prevFormData,
                        "Vehicle Color": e.target.value, // Update the correct key in formData
                      }))
                    }
                  />
                </div>
              </div>

              <div className="twoInputs">
                <div className="form-group">
                  <div className="conditionAndGradeLabelsContainer">
                    <label
                      className="titleForCondition"
                      htmlFor="preLossCondition"
                    >
                      Pre-Loss Condition
                    </label>
                    <label className="gradeLabel" htmlFor="preLossCondition">
                      Grade
                    </label>
                  </div>
                  <div className="preLossGradeContainer">
                    <select
                      className="halfInputCondition"
                      value={formData?.["Pre-Loss Condition"] || ""} // Safely access the selected condition
                      onChange={(e) =>
                        setFormData((prevFormData) => ({
                          ...prevFormData,
                          "Pre-Loss Condition": e.target.value, // Update the correct key in formData
                        }))
                      }
                    >
                      <option value="" disabled>
                        Select Condition
                      </option>
                      <option value="Brand New">Brand New</option>
                      <option value="Showroom">Showroom</option>
                      <option value="Restored Original">
                        Restored Original
                      </option>
                      <option value="Restomod">Restomod</option>
                      <option value="Excellent">Excellent</option>
                      <option value="Good">Good</option>
                      <option value="Fair">Fair</option>
                      <option value="Poor">Poor</option>
                      <option value="Inoperable">Inoperable</option>
                      <option value="Parts Car">Parts Car</option>
                      <option value="Junk">Junk</option>
                    </select>
                    <input
                      className="halfInputGrade"
                      type="number"
                      value={formData?.["Grade"] || ""} // Safely access grade
                      onChange={
                        (e) =>
                          setFormData({ ...formData, Grade: e.target.value }) // Update the correct key in formData
                      }
                      placeholder="Grade"
                    />
                  </div>
                </div>
                <div className="form-group" style={{ left: "-39px" }}>
                  <label className="titleForInformation" htmlFor="marketArea">
                    Market Area
                  </label>
                  <input
                    className="halfInput"
                    type="text"
                    name="Market Area" // Ensure this matches the key in formData
                    value={formData?.["Market Area"] || ""} // Bind to formData
                    onChange={handleInputChange} // Update state
                  />
                </div>
              </div>
            </div>
            <div className="form-group">
              <label className="titleForInformation" htmlFor="appraiserNotes">
                Appraiser Notes
              </label>
              <textarea
                className="appraisalNotes"
                id="appraiserNotes"
                placeholder="Insert notes"
                rows="5"
                value={notes.join("\n")}
                onChange={(e) => {
                  // Split text by new lines to create array
                  const newNotes = e.target.value.split("\n");
                  setNotes(newNotes);
                }}
                // Preserve Enter key behavior
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    setNotes((prev) => [...prev, ""]);
                  }
                }}
              />
            </div>
            <div className="form-group">
              <h2 className="titlesCertificate">Vehicle Picture</h2>
              <section className="form-section">
                <form>
                  <div className={`file-upload`}>
                    <input
                      type="file"
                      id="uploadBlackBook"
                      name="uploadBlackBook"
                      accept="image/*" // Restrict to image files
                      onChange={handleFileChange} // Handle file selection
                      multiple // Allow multiple files to be selected
                    />
                    <label
                      htmlFor="uploadBlackBook"
                      className="file-upload-button"
                    >
                      Select Files
                    </label>
                  </div>
                </form>
                <form>
                  <div className="selected-image-container-wrapper">
                    <div className="selected-image-container">
                      {Array.isArray(selectedImages) &&
                      selectedImages.length > 0 ? (
                        <div className="selected-images-row">
                          {selectedImages.map((image, index) => (
                            <div key={index} className="selected-image-item">
                              <img
                                src={image}
                                alt={`Selected for PDF ${index + 1}`}
                                className="selected-image-preview"
                              />
                              <button
                                type="button"
                                className="remove-image-button"
                                onClick={() => handleRemoveImage(index)}
                              >
                                X
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p>Select images for PDF</p> // Placeholder text
                      )}
                    </div>
                  </div>
                </form>
                <section>
                  <div className="imgsDiv">
                    {Array.isArray(files) && files.length > 0 ? (
                      files.map((file) => (
                        <div key={file.FileId} className="rowImg">
                          <p className="imgName">{file.FileName}</p>
                          <button
                            className="addToPdfButton"
                            type="button"
                            onClick={() => handleImageSelect(file)}
                          >
                            Add to PDF
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="no-images-message">
                        <p>{error || "No images to show"}</p>
                      </div>
                    )}
                  </div>
                </section>
              </section>
            </div>
          </form>
        </section>
      </div>
      <div>
        <Modal
          isOpen={isModalOpen}
          onRequestClose={closeModal}
          contentLabel="PDF Preview Modal"
          className="custom-modal"
          overlayClassName="custom-overlay"
        >
          <h2 className="certificatePdfPreviewheader">PDF Preview</h2>
          {pdfBlobUrl ? (
            <iframe
              src={pdfBlobUrl}
              title="PDF Preview"
              width="100%"
              height="500px"
              className="pdfPreview"
            ></iframe>
          ) : (
            <p>Loading preview...</p>
          )}
          <button className="close-button" onClick={closeModal}>
            Close
          </button>
        </Modal>
      </div>
    </div>
  );
};

export default AppraisalRequest;
